import { capitalize } from '@mui/material/utils';
import currencyFormatter from 'src/helpers/currencyFormatter';
import { PurchasePolicyInput } from 'src/redux/reducer/v2/purchasePolicy';

export interface CoverageData {
  label: string;
  value: string;
}

const coverageKeyToLabelMap = {
  personalPropertyCoverage: 'Personal property',
  personalLiabilityCoverage: 'Personal liability',
  medicalPayments: 'Medical liability',
  deductible: 'Deductible',
  waterBackup: 'Water backup',
  petDamage: 'Pet damages',
  lossOfUse: 'Loss of sse',
  theft: 'Theft deductible',
  identityFraud: 'Identity fraud',
  windHail: 'Wind and hail deductible',
  earthquake: 'Earthquake deductible',
  hurricane: 'Hurricane deductible',
};

const getUnkownCoverageLabel = (coverageKey: string) => {
  return capitalize(coverageKey.replace(/([A-Z])/g, ' $1').toLowerCase());
};

function getCoverageLabel(coverageKey: string) {
  return (
    coverageKeyToLabelMap[coverageKey as keyof typeof coverageKeyToLabelMap] ??
    getUnkownCoverageLabel(coverageKey)
  );
}

export const getCoverageData = (purchasePolicyInput: PurchasePolicyInput) => {
  const {
    personalPropertyCoverage,
    personalLiabilityCoverage,
    medicalPayments,
    deductible,
    heading,
    waterBackup,
    petDamage,
    lossOfUse,
    theft,
    identityFraud,
    windHail,
    earthquake,
    hurricane,
    optionalCoverages,
  } = purchasePolicyInput;

  const coverageData = {
    heading,
    personalPropertyCoverage,
    personalLiabilityCoverage,
    medicalPayments,
    deductible,
    waterBackup,
    petDamage,
    lossOfUse,
    theft,
    identityFraud,
    windHail,
    earthquake,
    hurricane,
    ...optionalCoverages,
  };

  const formattedCoverageData = Object.entries(coverageData).reduce(
    (acc, [key, value]) => {
      if (!value) {
        return acc;
      }

      const coverageKey = key as keyof typeof coverageData;
      const coverageLabel = getCoverageLabel(coverageKey);

      if (typeof value === 'boolean' && value === true) {
        acc.push({
          label: coverageLabel,
          value: 'Enabled',
        });
      } else if (typeof value === 'number') {
        acc.push({
          label: coverageLabel,
          value: currencyFormatter.format(value),
        });
      } else if (typeof value === 'string') {
        acc.push({
          label: coverageLabel,
          value: value,
        });
      }

      return acc;
    },
    [] as CoverageData[],
  );

  return formattedCoverageData;
};
